import { pickLang, useLanguage } from "@/i18n/LanguageContext";
import { GENERIC_STRINGS } from "@/pages/contact/requestFormsContent";

/**
 * Accessible label + input + inline error row used by all 4 request forms.
 *
 * Handles text, email, tel, textarea, and select inputs. Radios are handled
 * separately by `<PreferredReplyField />`.
 */
export default function FormField({ field, value, onChange, error }) {
  const { lang } = useLanguage();
  const g = GENERIC_STRINGS[lang];
  const id = `req-${field.name}`;
  const label = pickLang(field.label, lang);

  const commonProps = {
    id,
    name: field.name,
    value: value ?? "",
    onChange: (e) => onChange(field.name, e.target.value),
    "aria-invalid": error ? "true" : undefined,
    "aria-describedby": error ? `${id}-error` : undefined,
    "data-testid": `field-${field.name}`,
    className: "sas-input",
    dir: field.dirLtr ? "ltr" : undefined,
  };

  let control;
  if (field.type === "textarea") {
    control = (
      <textarea
        {...commonProps}
        rows={field.rows || 4}
        className="sas-input py-3"
        style={{ height: "auto", minHeight: `${(field.rows || 4) * 28}px`, resize: "vertical" }}
      />
    );
  } else if (field.type === "select") {
    control = (
      <select {...commonProps}>
        <option value="">{g.selectPlaceholder}</option>
        {(field.options || []).map((opt) => (
          <option key={opt.key} value={opt.key}>
            {pickLang(opt, lang)}
          </option>
        ))}
      </select>
    );
  } else if (field.type === "multiselect") {
    // Checkbox-pill group. `value` is a JS array of selected keys.
    // Rendered as toggleable chips so mobile users can tap and see all
    // options in one glance without opening a native <select> UI.
    const selected = Array.isArray(value) ? value : [];
    const toggle = (key) => {
      const next = selected.includes(key)
        ? selected.filter((k) => k !== key)
        : [...selected, key];
      onChange(field.name, next);
    };
    control = (
      <div
        id={id}
        role="group"
        aria-describedby={error ? `${id}-error` : undefined}
        aria-invalid={error ? "true" : undefined}
        className="flex flex-wrap gap-2"
        data-testid={`field-${field.name}`}
      >
        {(field.options || []).map((opt) => {
          const active = selected.includes(opt.key);
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => toggle(opt.key)}
              aria-pressed={active}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[13px] font-medium border transition-colors ${
                active
                  ? "bg-[color:var(--sas-accent)] text-white border-[color:var(--sas-accent)]"
                  : "bg-white text-[color:var(--sas-ink)] border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)]"
              }`}
              data-testid={`field-${field.name}-option-${opt.key}`}
            >
              {pickLang(opt, lang)}
            </button>
          );
        })}
      </div>
    );
  } else {
    control = (
      <input
        {...commonProps}
        type={field.type || "text"}
        autoComplete={field.autoComplete || "off"}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid={`field-row-${field.name}`}>
      <label htmlFor={id} className="text-[13px] font-semibold text-[color:var(--sas-ink)]">
        {label}
        {field.required ? (
          <span className="text-[color:#b91c1c] ms-1" aria-hidden>*</span>
        ) : (
          <span className="text-[color:var(--sas-ink-2)] ms-1 font-normal">{g.optional}</span>
        )}
      </label>
      {control}
      {error && (
        <p
          id={`${id}-error`}
          className="text-[12.5px] text-[#b91c1c]"
          data-testid={`field-error-${field.name}`}
        >
          {error}
        </p>
      )}
    </div>
  );
}
